import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import { useMemo } from "react";

import { type ExtendedExternalIntegrationType, ExtendedExternalIntegrationTypes } from "../types";
import { useGetWorkplaceExternalIntegrations } from "./useGetWorkplaceExternalIntegrations";

interface UseGetWorkplaceEnabledConfigurationsParams {
  workplaceId: string;
}

export function useGetWorkplaceEnabledConfigurations(
  params: UseGetWorkplaceEnabledConfigurationsParams
): ExtendedExternalIntegrationType[] {
  const { workplaceId } = params;

  const { data: externalIntegrations } = useGetWorkplaceExternalIntegrations({ workplaceId });

  const { enabled: touchpointIntegrationEnabled } = useCbhFlag(
    CbhFeatureFlag.TOUCHPOINT_INTEGRATION,
    {
      defaultValue: {
        enabled: false,
      },
    }
  );

  const { state: timeclockComplianceRolloutState } = useCbhFlag(
    CbhFeatureFlag.TIMECLOCK_COMPLIANCE_ROLLOUT,
    {
      defaultValue: {
        state: "disabled" as const,
      },
    }
  );

  const { state: timeclockVideosGateInstantpayState } = useCbhFlag(
    CbhFeatureFlag.TIMECLOCK_VIDEOS_GATE_INSTANTPAY,
    {
      defaultValue: {
        state: "disabled" as const,
      },
    }
  );

  const isTimeclockComplianceEnabled =
    timeclockComplianceRolloutState === "enabled" ||
    timeclockVideosGateInstantpayState === "enabled";

  return useMemo(() => {
    const types: ExtendedExternalIntegrationType[] = (externalIntegrations ?? [])
      .filter((integration) => integration.isEnabled)
      .map((integration) => integration.type);

    if (touchpointIntegrationEnabled) {
      types.push(ExtendedExternalIntegrationTypes.MESH);
    }

    if (
      isTimeclockComplianceEnabled &&
      !types.includes(ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE)
    ) {
      types.push(ExtendedExternalIntegrationTypes.TIMECLOCK_COMPLIANCE);
    }

    return types;
  }, [externalIntegrations, touchpointIntegrationEnabled, isTimeclockComplianceEnabled]);
}
